import { Injectable } from '@angular/core';
import { BaseOptionService } from '@luzula/luzula-lib';
import { FBF_PREFIX_REGEX } from '../data-model';
import { fbfPraefixCodes } from './help/fbf-praefixe';
import Humusformen from '../../assets/json/Humusformen.json';

function escapeRegex(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Nährkraft von arm (A) nach reich (R): A, Z, M, K, R. Alphabetisch stünde R vor Z
 * und K vor M; das Auswahlfeld im Reiter Boden ordnet nach dieser Rangfolge
 * (Nutzer-Vorgabe 15.09.2026). Die Varianten einer Stufe (M, MC, M+, Mv) stehen in
 * der Reihenfolge der gepflegten Liste „Nährkraftstufen".
 */
export const NAEHRKRAFT_RANGFOLGE = ['A', 'Z', 'M', 'K', 'R'];

@Injectable({
	providedIn: 'root'
})
export class OptionService extends BaseOptionService {
	// Humusform-Validierung = Sonderregel (Kombinatorik [sfimnt]+b1-8+Endung wie mb5Ro)
	// ODER exakter Listen-Eintrag. Default aus der gebündelten Liste; der Listen-Teil
	// wird vom DataService via setHumusFormList() aus dem zentralen Store aktualisiert.
	private humusFormRegExp = this.buildHumusFormRegExp(Humusformen as string[]);
  private FBFprefixRegExp = FBF_PREFIX_REGEX;
  private farbeRegExp = "^(rostf|ock|sch|bg|br|ro|fa|gl|gr|gn|h|d|w)*$";

	/** Baut die Humusform-Regex: Sonderregel (Kombinatorik) ODER Listen-Eintrag. */
	private buildHumusFormRegExp(list: string[]): string {
		return `^(([sfimnt])(b[1-8])?(Hu|Ma|Ro|RM|Mo|MM|Mu|aÖ|zÖ|mÖ|kÖ|rÖ)|${list.map(escapeRegex).join('|')})$`;
	}

	/**
	 * Aktualisiert den Listen-Teil der Humusform-Regex aus dem zentralen Store.
	 * Wird vom DataService bei Store-Hydrierung/-Änderung aufgerufen. Die Sonderregel
	 * bleibt erhalten. Wirkt für neu geöffnete Felder (Optionen werden beim Init
	 * gelesen -> bei offener Maske ggf. erst nach Reload sichtbar).
	 */
	setHumusFormList(list: string[]): void {
		if (!list?.length) return;
		this.humusFormRegExp = this.buildHumusFormRegExp(list);
		this.registerOption("HumusformNeu", { regex: this.humusFormRegExp, regexFailMessage: this.humusFormRegexpMessage });
	}

  constructor() {
		super();
    // Options: sollen die html-templates der View- und Input-Elemente vereinfachen
		
		// Maskierte Felder: 
		//this.registerOption("Kartierer", { masked: true });
		
    // <bp-list...>
    this.registerOption("Koordinatensystem", { tableName: "Koordinatensysteme" });
		// Punktart: type ist seit v16 die reine Nummer; der Typeahead zeigt/sucht
		// das Label "Beschreibung (Nummer)" (abgeleitete Spalte `label`, von
		// DataService.getHardcoded ergaenzt) — so ist der Key sichtbar und durch
		// Tippen der Nummer suchbar. Label<->Nummer-Abbildung macht die Komponente
		// (bohrpunkt-neu / aufnahme / kurzprotokoll) ueber das data-model-Lookup.
		// acceptOtherValues: ein nicht (mehr) in der Liste vorhandener Wert (Alt-Punkt
		// mit entfernter Punktart) erzeugt KEINEN harten "Wert nicht in der Liste"-
		// Fehler; stattdessen liefert der Punktart-Validator (ValidationService) nur
		// eine Warnung, sodass der Punkt editierbar/anpassbar bleibt.
		this.registerOption("Punktart", { tableName: "Punktarten", tableColumn: "label", codeSort: true, acceptOtherValues: true });
    this.registerOption("PunktartMitProtokoll", { tableName: "Punktarten", tableColumn: "label", codeSort: true, acceptOtherValues: true });
    this.registerOption("Unterabteilung", { options: "a|b|c|d|e|f|g|h|i|j|k|l|m|n|o|p|q|r|s|t|u|v|w|x|y|z" });
    this.registerOption("Entwässerungsverhalten", { options: "I|II|III" });
    this.registerOption("Substrat", { tableName: "Bodenarten", placement: "right-top" });
    this.registerOption("Horizont1", { tableName: "HorizontSymbole" });
    this.registerOption("Horizont2", { tableName: "HorizontSymbole" });
    this.registerOption("Horizont3", { tableName: "HorizontSymbole" });
    this.registerOption("Humusgehalt", { options: "|0|1|2|3|4|5|6|7" });
    this.registerOption("Lagerungsdichte", { options: "|1|2|3" });
    this.registerOption("Skelettgehalt", { options: "|0|1|2|3|4|5|6|7" });
    this.registerOption("Karbonat", { options: "|0|1|2|3|4|5|6" });
    this.registerOption("PHOrt", { options: "|Oberboden|Saugsaum" });
    // Mesoklima kommt seit 29.08.2026 aus der pflegbaren Liste (Listenverwaltung
    // -> Bereich „inline"), nicht mehr aus einem fest verdrahteten options-String.
    // Vorher stand "|fr|tr" hier doppelt UND noch einmal im Naturraum-Template —
    // ein fehlender Wert liess sich nirgends nachtragen.
    // Angezeigt wird "fr (frischer)", gespeichert weiter nur "fr" — das Kuerzel
    // haengt an der Bodenform ("...-fr"). Die Trennung macht das Auswahlfeld
    // ueber tableColumn/displayColumn (siehe listenverwaltung/liste-mit-erklaerung.ts).
    this.registerOption("Mesoklima", { tableName: "Mesoklima", tableColumn: "wert", displayColumn: "anzeige" });
    this.registerOption("GeologieTrenner", { options: "|/|//" });
    this.registerOption("KlimastufeAlt", { tableName: "Klimastufen", acceptOtherValues: true });
    this.registerOption("KlimastufeNeu", { tableName: "Klimastufen", acceptOtherValues: true });
    this.registerOption("Vegetation", { tableName: "Bodenvegetationen", expandOnClick: false, maxItems: 20 });
    this.registerOption("Vegetationsschicht", { options: "|b1|b2|str|kr1|kr2|m" });
    this.registerOption("Ökologischer Moortyp", { tableName: "OekologischerMoortyp" });
    this.registerOption("Hydrogenetischer Moortyp", { tableName: "HydrogenetischerMoortyp" });
		this.registerOption("Nährkraftstufe", { tableName: "Nährkraftstufen", rangfolge: NAEHRKRAFT_RANGFOLGE } );
		this.registerOption("Stammfeuchtestufe1", { options: "|O|N|Ü|W" } );
		this.registerOption("Stammfeuchtestufe2", { options: "|3|3w|2|2g|2w|1|1w|0|4|4w|3ü|4ü" } );
		this.registerOption("Substratgruppe", { tableName: "Substrattypengruppen", acceptOtherValues: true } );
		this.registerOption("KMgCaP-Serie", { from: "dietmarsMdb", tableName: "zcd_Serie", tableColumn: "Serie_k" } );
		this.registerOption("V-Wert-Vermutet", { options: "|<15|15-40|>40" } );
		this.registerOption("Torf-Zersetzungsgrad", { options: "|1|2|3|4|5|6|7|8|9|10" } );
		this.registerOption("Mudde-Konsistenz", { options: "|1|2|3|4|5" } );
		this.registerOption("Substanzvolumen", { options: "|1|2|3|4|5" } );
		this.registerOption("Reliefform Bestand", { options: "|rasig|bültig" } );
		this.registerOption("Reliefform Bodenvegetation", { options: "|rasig|bültig" } );

		// Grubenprotokoll — Schicht-Optionen (SEA Teil B "Weisergrubenaufnahme")
		this.registerOption("Festigkeit", { options: "|0|1|2|3|4" });
		this.registerOption("GefuegeForm", { tableName: "GefuegeForm", acceptOtherValues: true });
		this.registerOption("GefuegeGroesse", { options: "|skl|kl|m|gr|sgr", acceptOtherValues: true });
		this.registerOption("GefuegeAuspraegung", { options: "|schwach|mäßig|stark", acceptOtherValues: true });
		this.registerOption("FeindurchwurzelungDichte", { options: "|1a|1b|2a|2b|3|4|5|6", acceptOtherValues: true });
		this.registerOption("FeindurchwurzelungHaeufung", { options: "|1|2|3|4" });
		this.registerOption("Perstruktionszone", { options: "|α|β|γ|δ|ε|ζ|η" });
		this.registerOption("SchichtLage", { acceptOtherValues: true });

		// Grubenprotokoll — Kopffeld-Optionen (hartkodierte SEA-Listen, keine Dietmar-MDB)
		this.registerOption("Klimabereich", { tableName: "Klimabereich", acceptOtherValues: true });
		this.registerOption("Bestockungstyp", { tableName: "Bestockungstyp", acceptOtherValues: true });
		this.registerOption("Begründungsart", { tableName: "Begruendungsart", acceptOtherValues: true });
		this.registerOption("Mischform", { tableName: "Mischform", acceptOtherValues: true });
		this.registerOption("Waldfunktion", { tableName: "Waldfunktion", acceptOtherValues: true });
		this.registerOption("ZustandsSTFG", { tableName: "ZustandsSTFG", acceptOtherValues: true });
		this.registerOption("BaumartArt", { tableName: "BaumartArt", acceptOtherValues: true });
		this.registerOption("HumusformGesamt", { tableName: "Humusformen", acceptOtherValues: true });
		this.registerOption("HumusformMorphologisch", { tableName: "Humusformen", acceptOtherValues: true });

		// Relief-Erweiterung nach SEA
		this.registerOption("Neigungsstufe", { tableName: "Neigungsstufe", tableColumn: "id", displayColumn: "label" } );
		this.registerOption("Woelbung", { tableName: "Woelbung", tableColumn: "id", displayColumn: "label" } );
		this.registerOption("Hangrichtung", { tableName: "Hangrichtung", tableColumn: "id", displayColumn: "label" } );
		this.registerOption("Hanganschluss", { tableName: "Hanganschluss", tableColumn: "id", displayColumn: "label" } );
		this.registerOption("Hangteil", { tableName: "Hangteil" } );
		this.registerOption("InnereRauhheit", { tableName: "InnereRauhheit", tableColumn: "id", displayColumn: "label" } );

		this.registerOption("pH", { min: 0, max: 14 });

    // nonEmpty (Pflichtfelder mit roter Validierung)
    const nonEmptyFields = new Array<string>(...[
      "Punktart", "Forstamt", "Aufnahmedatum", "Kartierer", "Feinbodenform", "Entwässerungsverhalten",
      "X-Koordinate", "Y-Koordinate"
    ]);
    this.registerOptionFor(nonEmptyFields, { nonEmpty: true });
    // Nummer darf leer bleiben, wird aber als Warnung (gelb) markiert
    this.registerOption("Nummer", { nonEmpty: true, nonEmptySeverity: 'warning' });

    // typeaheads
    this.registerOption("Forstamt", { tableName: "Forstämter" });
    this.registerOption("Revier", { tableName: "Reviere", tableColumn: "FORSTREVIE" });
    this.registerOption("Hauptbodenform", { tableName: "Feinbodenformen", tableColumn: "HBF" });
    this.registerOption("Feinbodenform", { tableName: "Feinbodenformen", tableColumn: "FBF", maxItems: 50 });
    this.registerOption("BodenformSuffix", { tableName: "BodenformSuffixe", tableColumn: "value" });
    this.registerOption("Reliefform", { from: "dietmarsMdb", tableName: "zda_RelFo", tableColumn: "RelFo_t", acceptOtherValues: true });
    this.registerOption("ReliefformUmgebung", { tableName: "ReliefformUmgebung" });
    this.registerOption("GeologieOben", { from: "dietmarsMdb", tableName: "zca_GeEinh", tableColumn: "GeEinh_p" });
    this.registerOption("GeologieUnten", { from: "dietmarsMdb", tableName: "zca_GeEinh", tableColumn: "GeEinh_p" });
    this.registerOption("Morphologie0", {  from: "dietmarsMdb", tableName: "zcb_GeMor", tableColumn: "GeMor_t" });
    this.registerOption("Morphologie1", {  from: "dietmarsMdb", tableName: "zcb_GeMor", tableColumn: "GeMor_t" });
    this.registerOption("HumusformAlt", { tableName: "Humusformen" });
    this.registerOption("HumusformNeu", { tableName: "Humusformen" });
    this.registerOption("Vegetationsformengruppe", { tableName: "Humusformen" });
    this.registerOption("Qualitätsstufe", { tableName: "Qualitätsstufen", tableColumn: "id", displayColumn: "name" });
		this.registerOption("Torfart1", { tableName: "Torfarten", tableColumn: "value" } );
		this.registerOption("Torfart2", { tableName: "Torfarten", tableColumn: "value" } );
		this.registerOption("Torfart3", { tableName: "Torfarten", tableColumn: "value" } );
		this.registerOption("Wuchsgebiet", { tableName: "Wuchsgebiete" } );
		this.registerOption("Wuchsbezirk", { tableName: "Wuchsbezirke", tableColumn: "WB" } );
		
		// this.registerOption("Bestandsbeschreibung", { tableName: "Bestandsbeschreibungen", tableColumn: "ID" } );

    // regular expressions
    this.registerOption("HumusformNeu", { regex: this.humusFormRegExp, regexFailMessage: this.humusFormRegexpMessage });
    this.registerOption("FBFPrefix", { regex: this.FBFprefixRegExp, regexFailMessage: this.fbfPrefixRegexpMessage });
    this.registerOption("Farbe", { regex: this.farbeRegExp, regexFailMessage: this.farbeRegexpMessage });
  
    // acceptOtherValues
    const acceptOtherValuesFieldFields = new Array<string>(...[
      "Revier", "HumusformAlt", "HumusformNeu"
    ]);
    this.registerOptionFor(acceptOtherValuesFieldFields, { acceptOtherValues: true });

    // cm
    const cmFields = new Array<string>(...[
      "Tiefe", "Moormächtigkeit",
      "Go", "Gr", "G", "Sw", "Sd", "S",
    ]);
    this.registerOptionFor(cmFields, { unit: "cm" });

    // %
    const percFields = new Array<string>(...[
      "Schichtanteilb1", "Schichtanteilb2", "Schichtanteilstr", "Schichtanteilkr1", "Schichtanteilkr2", "Schichtanteilm",
    ]);
    this.registerOptionFor(percFields, { unit: "%", min: 0, max: 100 });
  }

  // Regex-Message Funktionen für Bohrpunkte-spezifische Validierungen
  humusFormRegexpMessage(str: string, regex: string): string {
    const matches = str.match(regex);
    if (matches) return "";
    else {
      return "Wert muss in der Humusformen-/Vegetationsformengruppen-Liste enthalten sein.";
    }
  }

  fbfPrefixRegexpMessage(str: string, regex: string): string {
    const matches = str.match(regex);
    if (matches) return "";
    else {
      return `Wert muss eine Aufeinanderfolge aus ${fbfPraefixCodes()} sein.`;
    }
  }


  farbeRegexpMessage(str: string, regex: string): string {
    const matches = str.match(regex);
    if (matches) return "";
    else {
      return "Nur zulässige Kombinationen.";
    }
  }
}
